import { DEFAULT_SPREAD } from '@ui/constants/uiConfig';
import { SlippageType } from '@ui/services/contract/hooks/PairsManagerFacet/types';
import { TradePair } from '@ui/types/tradePair';
import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';

export const useSpreadPercentage = ({
  tradePair,
  positionSize,
  pairPrice,
}: {
  tradePair: Nullable<TradePair>;
  positionSize: Nullable<BigNumber>;
  pairPrice: Nullable<BigNumber>;
}) => {
  return useMemo(() => {
    return getSpreadPercentageInfo({
      tradePair,
      positionSize,
      pairPrice,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pairPrice,
    positionSize,
    tradePair, // update when ever longQty / shortQty and slippageConfig change
    tradePair?.longQty,
    tradePair?.shortQty,
    tradePair?.slippageConfig,
  ]);
};

// for creating long positions:
// spread = (longOI + currentPositionSize) / onePercentDepthAboveUsd + 0.01%

// for creating short positions:
// spread = (shortOI + currentPositionSize) / onePercentDepthBelowUsd + 0.01%
export const getSpreadPercentageForOnePercentageDepth = ({
  oi,
  size,
  onePercentDepth,
}: {
  oi: BigNumber;
  size: BigNumber;
  onePercentDepth: BigNumber;
}): number => {
  return oi.plus(size).div(onePercentDepth).plus(DEFAULT_SPREAD).toNumber();
};

export const getSpreadPercentageInfo = ({
  tradePair,
  positionSize,
  pairPrice,
}: {
  tradePair: Nullable<TradePair>;
  positionSize: Nullable<BigNumber>;
  pairPrice: Nullable<BigNumber>;
}) => {
  if (!tradePair || !positionSize || positionSize.isNaN() || !pairPrice) {
    return {
      longSpreadPercentage: DEFAULT_SPREAD,
      shortSpreadPercentage: DEFAULT_SPREAD,
    };
  }

  const slippageConfig = tradePair.slippageConfig;
  let longSpreadPercentage: Nullable<number>,
    shortSpreadPercentage: Nullable<number>;

  const longQty = tradePair.longQty; // current long OI
  const shortQty = tradePair.shortQty; // current short OI
  const onePercentDepthAboveUsd = slippageConfig.onePercentDepthAboveUsd;
  const onePercentDepthBelowUsd = slippageConfig.onePercentDepthBelowUsd;

  switch (slippageConfig.slippageType) {
    case SlippageType.ONE_PERCENT_DEPTH:
      longSpreadPercentage = getSpreadPercentageForOnePercentageDepth({
        oi: longQty.multipliedBy(pairPrice),
        size: positionSize,
        onePercentDepth: onePercentDepthAboveUsd,
      });

      shortSpreadPercentage = getSpreadPercentageForOnePercentageDepth({
        oi: shortQty.multipliedBy(pairPrice),
        size: positionSize,
        onePercentDepth: onePercentDepthBelowUsd,
      });

      break;
    case SlippageType.FIXED:
    default:
      longSpreadPercentage = slippageConfig.slippageLongP;
      shortSpreadPercentage = slippageConfig.slippageShortP;
  }

  return {
    longSpreadPercentage,
    shortSpreadPercentage,
  };
};
/**
 * @params isClosePrice
 * - true: for TP/SL, close price, use the reverse spread percentage for TP/SL
 * - false: for none close price relative
 */
export const calculateSpreadPercentage = ({
  isLong,
  isClosePrice,
  spreadPercentageInfo,
}: {
  isLong: boolean;
  isClosePrice: boolean;
  spreadPercentageInfo: ReturnType<typeof getSpreadPercentageInfo>;
}) => {
  const { longSpreadPercentage, shortSpreadPercentage } = spreadPercentageInfo;

  return isClosePrice
    ? isLong
      ? shortSpreadPercentage
      : longSpreadPercentage
    : isLong
    ? longSpreadPercentage
    : shortSpreadPercentage;
};
